// One canonical embryo identity per row; reported facts and diseases are columns.
import { useState } from "react";
import { ScrollView, View, type TextStyle } from "react-native";
import {
  CardTitleText,
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogHeader,
  MetaText,
  MoonCheckbox,
  Row,
  SectionTitleRow,
  Tag,
  cardSurface,
} from "@louise/moon-ui";
import { typography } from "@louise/moon-ui/theme/tokens";
import {
  describeEmbryoEntryName,
  describeEmbryoEntryStatus,
  describeEmbryoResultReason,
  formatEmbryoDate,
  getEmbryoResultReason,
  type EmbryoEntry,
  type EmbryoTransferRecommendation,
} from "@src/packages/care/cryovault/embryo-detail";
import {
  EMBRYO_COMPARISON_IDENTITY_WIDTH,
  getEmbryoComparisonColumnWidths,
  getEmbryoComparisonColumns,
  getEmbryoComparisonHeaderHeight,
  getEmbryoComparisonRowHeight,
  visibleEmbryoComparisonColumns,
  type EmbryoComparisonColumn,
} from "@src/packages/care/cryovault/embryo-detail/embryoComparisonColumns";
import type { EmbryoReport } from "@src/packages/cycles/hooks/useEmbryoReport";
import { OrganicMiniIcon } from "@src/packages/homepage/plan/components/OrganicMiniIcon";
import type { TranslationFunction } from "@src/packages/i18n";

export type ComparedEmbryo = EmbryoEntry & { report: EmbryoReport };
const CELL_PADDING_HORIZONTAL = 3;
const INK = "#303437";
const SECONDARY = "#73777A";
const DIVIDER = "rgba(48,52,55,0.08)";
const TABLE_TEXT_STYLE: TextStyle = {
  fontFamily: typography.families.sans,
  fontSize: typography.sizes.sm,
  lineHeight: 14,
  fontWeight: "400",
};
const TABLE_HEADER_TEXT_STYLE: TextStyle = {
  ...TABLE_TEXT_STYLE,
  fontFamily: typography.families.sansMedium,
};
const TABLE_META_TEXT_STYLE: TextStyle = {
  ...TABLE_TEXT_STYLE,
  fontSize: typography.sizes.xs,
  lineHeight: 12,
  color: SECONDARY,
  marginTop: 0,
};
const TABLE_TAG_TEXT_STYLE: TextStyle = {
  ...TABLE_HEADER_TEXT_STYLE,
  fontSize: typography.sizes.xs,
  lineHeight: 12,
  flexShrink: 1,
};

const RECOMMENDATION_KEYS: Record<EmbryoTransferRecommendation, string> = {
  yes: "care.embryo_report.compare.recommended",
  with_counseling: "care.embryo_report.compare.after_counseling",
  no: "care.embryo_report.compare.not_recommended",
};

function columnLabel(column: EmbryoComparisonColumn, t: TranslationFunction) {
  return column.condition ?? (column.labelKey ? t(column.labelKey) : "");
}

function columnRisk(embryo: ComparedEmbryo, column: EmbryoComparisonColumn) {
  return embryo.kind === "tested"
    ? embryo.embryo.polygenicRisks.find(
        (risk) => risk.condition === column.condition,
      )
    : undefined;
}

function ColumnValue({
  embryo,
  column,
  showPopulation,
  t,
}: {
  embryo: ComparedEmbryo;
  column: EmbryoComparisonColumn;
  showPopulation: boolean;
  t: TranslationFunction;
}) {
  if (column.group === "polygenic") {
    const risk = columnRisk(embryo, column);
    return (
      <View style={{ gap: 2 }}>
        <MetaText
          style={{
            ...TABLE_TEXT_STYLE,
            color: INK,
            marginTop: 0,
            textAlign: "center",
          }}
        >
          {risk?.absoluteRisk ?? "–"}
        </MetaText>
        {risk && showPopulation && (
          <MetaText style={{ ...TABLE_META_TEXT_STYLE, textAlign: "center" }}>
            {t("care.embryo_detail.population_average_risk", {
              params: { risk: risk.populationRisk },
            })}
          </MetaText>
        )}
      </View>
    );
  }
  let value: string | null = null;
  if (column.key === "grade") {
    value = embryo.kind === "tested" ? embryo.embryo.grade : embryo.grade;
  }
  if (column.key === "day") {
    const day = embryo.kind === "tested" ? embryo.embryo.biopsyDay : embryo.day;
    value =
      day != null
        ? t("care.embryo_report.day_label", { params: { day: String(day) } })
        : null;
  }
  if (embryo.kind === "tested") {
    if (column.key === "finding") {
      const reason = getEmbryoResultReason(embryo.embryo);
      value = reason
        ? describeEmbryoResultReason(reason, t)
        : t("care.embryo_report.compare.no_chromosome_finding");
    }
    if (column.key === "recommendation") {
      value = t(RECOMMENDATION_KEYS[embryo.embryo.transferRecommendation]);
    }
    if (column.group === "single_gene") {
      value =
        embryo.embryo.monogenicFindings
          .filter((finding) => finding.condition === column.condition)
          .map((finding) =>
            finding.status === "unaffected"
              ? t("care.embryo_report.compare.unaffected")
              : t(
                  finding.status === "affected"
                    ? "care.embryo_report.row_finding.affected_by_condition"
                    : "care.embryo_report.row_finding.carrier_for_condition",
                  { params: { condition: finding.condition } },
                ),
          )
          .join(" · ") || null;
    }
  }
  return (
    <MetaText
      numberOfLines={4}
      style={{
        ...TABLE_TEXT_STYLE,
        color: value ? INK : SECONDARY,
        marginTop: 0,
        textAlign:
          column.key === "grade" || column.key === "day" ? "center" : undefined,
      }}
    >
      {value ?? "–"}
    </MetaText>
  );
}

export default function EmbryoComparisonTable({
  embryos,
  showBatch,
  batchDateOf,
  onSelect,
  t,
  locale,
  columnVisibility,
  onColumnVisibilityChange,
  customizing,
  onCloseCustomization,
}: {
  embryos: ComparedEmbryo[];
  showBatch: boolean;
  batchDateOf: (report: EmbryoReport) => string | null;
  onSelect: (embryo: ComparedEmbryo) => void;
  t: TranslationFunction;
  locale: string;
  columnVisibility: Readonly<Record<string, boolean>>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  customizing: boolean;
  onCloseCustomization: () => void;
}) {
  const [columnsViewportWidth, setColumnsViewportWidth] = useState(0);
  const [headerContentHeights, setHeaderContentHeights] = useState<
    Record<string, number>
  >({});
  const [contentHeights, setContentHeights] = useState<
    Record<string, Record<string, number>>
  >({});
  const tested = embryos.flatMap((entry) =>
    entry.kind === "tested" ? [entry.embryo] : [],
  );
  const available = getEmbryoComparisonColumns(tested, {
    hasGrade: embryos.some((entry) =>
      entry.kind === "tested" ? !!entry.embryo.grade : !!entry.grade,
    ),
    hasDay: embryos.some((entry) =>
      entry.kind === "tested"
        ? entry.embryo.biopsyDay != null
        : entry.day != null,
    ),
  });
  const columns = visibleEmbryoComparisonColumns(available, columnVisibility);
  const columnWidths = getEmbryoComparisonColumnWidths(
    columns,
    columnsViewportWidth,
  );
  const headerHeight = getEmbryoComparisonHeaderHeight(
    columns.map((column) => headerContentHeights[column.key] ?? 0),
  );
  const measureContent = (embryoKey: string, field: string, height: number) => {
    const measured = Math.ceil(height);
    setContentHeights((previous) => {
      if (previous[embryoKey]?.[field] === measured) return previous;
      return {
        ...previous,
        [embryoKey]: { ...previous[embryoKey], [field]: measured },
      };
    });
  };
  const rowHeight = (embryoKey: string) =>
    getEmbryoComparisonRowHeight(
      contentHeights[embryoKey]?.identity ?? 0,
      columns.map((column) => contentHeights[embryoKey]?.[column.key] ?? 0),
    );
  const identityWidth = EMBRYO_COMPARISON_IDENTITY_WIDTH;
  const groups = ["embryology", "single_gene", "polygenic"] as const;
  const populationReferences = (column: EmbryoComparisonColumn) =>
    column.group === "polygenic"
      ? [
          ...new Set(
            embryos.flatMap((embryo) => {
              const risk = columnRisk(embryo, column);
              return risk?.populationRisk ? [risk.populationRisk] : [];
            }),
          ),
        ]
      : [];

  return (
    <View testID="embryo-comparison-table" style={{ gap: 12 }}>
      <View
        style={{
          ...cardSurface(),
          width: "100%",
          padding: 0,
          overflow: "hidden",
        }}
      >
        <View style={{ flexDirection: "row" }}>
          <View
            testID="embryo-comparison-identities"
            style={{
              width: identityWidth,
              borderRightWidth: 1,
              borderRightColor: DIVIDER,
            }}
          >
            <View
              style={{
                height: headerHeight,
                justifyContent: "center",
                paddingHorizontal: CELL_PADDING_HORIZONTAL,
                borderBottomWidth: 1,
                borderBottomColor: DIVIDER,
              }}
            >
              <CardTitleText
                style={{
                  ...TABLE_HEADER_TEXT_STYLE,
                  color: INK,
                  textAlign: "center",
                }}
              >
                {t("care.embryo_report.compare.embryo_identity")}
              </CardTitleText>
              <MetaText
                style={{
                  ...TABLE_META_TEXT_STYLE,
                  marginTop: 2,
                  textAlign: "center",
                }}
              >
                PGT-A
              </MetaText>
            </View>
            {embryos.map((embryo) => {
              const name = describeEmbryoEntryName(embryo, t);
              const status = describeEmbryoEntryStatus(embryo, t);
              const batchDate = showBatch
                ? formatEmbryoDate(batchDateOf(embryo.report), locale)
                : null;
              return (
                <Row
                  key={embryo.key}
                  surface="plain"
                  elevated={false}
                  density="dense"
                  leading={<OrganicMiniIcon kind="genetics" size={20} />}
                  leadingSize={20}
                  gap={4}
                  paddingHorizontal={CELL_PADDING_HORIZONTAL}
                  paddingVertical={4}
                  title={
                    <View
                      style={{
                        width: "100%",
                        gap: 2,
                        alignItems: "flex-start",
                      }}
                      onLayout={(event) =>
                        measureContent(
                          embryo.key,
                          "identity",
                          event.nativeEvent.layout.height,
                        )
                      }
                    >
                      <CardTitleText
                        numberOfLines={2}
                        style={{
                          ...TABLE_TEXT_STYLE,
                          color: INK,
                        }}
                      >
                        {name}
                      </CardTitleText>
                      <Tag
                        label={status.label}
                        tone={status.tone}
                        style={{ maxWidth: "100%", paddingHorizontal: 3 }}
                        textStyle={TABLE_TAG_TEXT_STYLE}
                      />
                      {embryo.storage?.kind === "transferred" && (
                        <Tag
                          label={t(
                            "care.embryo_report.storage_status.transferred",
                          )}
                          tone="neutral"
                          style={{ maxWidth: "100%", paddingHorizontal: 3 }}
                          textStyle={TABLE_TAG_TEXT_STYLE}
                        />
                      )}
                      {batchDate && (
                        <MetaText
                          numberOfLines={1}
                          style={TABLE_META_TEXT_STYLE}
                        >
                          {batchDate}
                        </MetaText>
                      )}
                    </View>
                  }
                  chevron={false}
                  onPress={() => onSelect(embryo)}
                  action="open_embryo_detail_from_comparison"
                  accessibilityLabel={
                    batchDate ? name + ", " + batchDate : name
                  }
                  testID={"embryo-comparison-identity-" + embryo.key}
                  style={{
                    height: rowHeight(embryo.key),
                    borderRadius: 0,
                    borderBottomWidth: 1,
                    borderBottomColor: DIVIDER,
                  }}
                />
              );
            })}
          </View>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            testID="embryo-comparison-columns"
            style={{ flex: 1 }}
            contentContainerStyle={{ flexGrow: 1 }}
            onLayout={(event) =>
              setColumnsViewportWidth(event.nativeEvent.layout.width)
            }
          >
            <View style={{ flexDirection: "row", flexGrow: 1 }}>
              {columns.map((column, index) => {
                const references = populationReferences(column);
                const population =
                  references.length === 1 ? references[0] : undefined;
                const headerAlignment =
                  column.group === "polygenic" ||
                  column.key === "grade" ||
                  column.key === "day"
                    ? "center"
                    : undefined;
                return (
                  <View
                    key={column.key}
                    testID={"embryo-comparison-column-" + column.key}
                    style={{ width: columnWidths[index] }}
                  >
                    <View
                      style={{
                        height: headerHeight,
                        justifyContent: "center",
                        paddingHorizontal: CELL_PADDING_HORIZONTAL,
                        borderBottomWidth: 1,
                        borderBottomColor: DIVIDER,
                      }}
                    >
                      <View
                        style={{ gap: 2 }}
                        onLayout={(event) => {
                          const height = Math.ceil(
                            event.nativeEvent.layout.height,
                          );
                          setHeaderContentHeights((previous) =>
                            previous[column.key] === height
                              ? previous
                              : { ...previous, [column.key]: height },
                          );
                        }}
                      >
                        <CardTitleText
                          numberOfLines={2}
                          style={{
                            ...TABLE_HEADER_TEXT_STYLE,
                            color: INK,
                            textAlign: headerAlignment,
                          }}
                        >
                          {columnLabel(column, t)}
                        </CardTitleText>
                        {population && (
                          <MetaText
                            style={{
                              ...TABLE_META_TEXT_STYLE,
                              textAlign: headerAlignment,
                            }}
                          >
                            {t("care.embryo_detail.population_average_risk", {
                              params: { risk: population },
                            })}
                          </MetaText>
                        )}
                      </View>
                    </View>
                    {embryos.map((embryo) => (
                      <View
                        key={embryo.key}
                        testID={
                          "embryo-comparison-cell-" +
                          embryo.key +
                          "-" +
                          column.key
                        }
                        style={{
                          height: rowHeight(embryo.key),
                          justifyContent: "center",
                          paddingHorizontal: CELL_PADDING_HORIZONTAL,
                          borderBottomWidth: 1,
                          borderBottomColor: DIVIDER,
                        }}
                      >
                        <View
                          onLayout={(event) =>
                            measureContent(
                              embryo.key,
                              column.key,
                              event.nativeEvent.layout.height,
                            )
                          }
                        >
                          <ColumnValue
                            embryo={embryo}
                            column={column}
                            showPopulation={references.length > 1}
                            t={t}
                          />
                        </View>
                      </View>
                    ))}
                  </View>
                );
              })}
              {columns.length === 0 && (
                <View style={{ flexGrow: 1 }}>
                  <View
                    style={{
                      height: headerHeight,
                      borderBottomWidth: 1,
                      borderBottomColor: DIVIDER,
                    }}
                  />
                  {embryos.map((embryo) => (
                    <View
                      key={embryo.key}
                      testID={"embryo-comparison-row-fill-" + embryo.key}
                      style={{
                        height: rowHeight(embryo.key),
                        borderBottomWidth: 1,
                        borderBottomColor: DIVIDER,
                      }}
                    />
                  ))}
                </View>
              )}
            </View>
          </ScrollView>
        </View>
      </View>
      {columns.length === 0 && (
        <MetaText style={{ color: SECONDARY }}>
          {t("care.embryo_report.compare.choose_fields_to_compare")}
        </MetaText>
      )}
      <Dialog
        visible={customizing}
        onClose={onCloseCustomization}
        layout="bottom"
        closeButton="minimal"
        testID="embryo-comparison-columns-dialog"
      >
        <DialogHeader
          title={t("care.embryo_report.compare.choose_columns")}
          subtitle={t("care.embryo_report.compare.choose_fields_and_diseases")}
          style={{ marginBottom: 20 }}
        />
        <ScrollView
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 18, paddingBottom: 16 }}
        >
          {groups.map((group) => {
            const choices = available.filter(
              (column) => column.group === group,
            );
            if (!choices.length) return null;
            return (
              <View key={group}>
                <SectionTitleRow
                  title={t("care.embryo_report.compare." + group + "_columns")}
                  style={{ paddingHorizontal: 0 }}
                />
                <View style={{ gap: 16 }}>
                  {choices.map((column) => (
                    <MoonCheckbox
                      key={column.key}
                      label={columnLabel(column, t)}
                      value={
                        columnVisibility[column.key] ?? column.defaultVisible
                      }
                      accentColor={INK}
                      onChange={(visible) =>
                        onColumnVisibilityChange({
                          ...columnVisibility,
                          [column.key]: visible,
                        })
                      }
                      action="toggle_embryo_comparison_column"
                      testID={"embryo-comparison-toggle-" + column.key}
                      style={{ minHeight: 28 }}
                    />
                  ))}
                </View>
              </View>
            );
          })}
        </ScrollView>
        <DialogActions>
          <CompactActionButton
            label={t("care.embryo_report.compare.reset_columns")}
            variant="secondary"
            onPress={() => onColumnVisibilityChange({})}
            action="reset_embryo_comparison_columns"
            style={{ flex: 1 }}
          />
          <CompactActionButton
            label={t("care.embryo_report.compare.finish_choosing_columns")}
            variant="selected"
            onPress={onCloseCustomization}
            action="close_embryo_comparison_columns"
            style={{ flex: 1 }}
          />
        </DialogActions>
      </Dialog>
    </View>
  );
}
